import React, { useState, useEffect } from "react";
import { Icon } from "./Icon";
import axios from "axios";

interface BotHealthStatus {
  ircConnected: boolean;
  eventSubHealthy: boolean;
  userTokenValid: boolean;
  lastPing?: string;
  issues: string[];
}

interface ActiveBot {
  id: string;
  username: string;
  channel: string;
  botUsername: string;
  isActive: boolean;
  isConnected: boolean;
  isFollowing: boolean;
  isMod: boolean;
  health: BotHealthStatus;
  userId: string;
  createdAt: string;
  updatedAt: string;
  debugEnabled?: boolean;
  lastActivity?: string;
  userApproved?: boolean;
}

interface BotListProps {
  authToken: string;
}

const BotList: React.FC<BotListProps> = ({ authToken }) => {
  const [bots, setBots] = useState<ActiveBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [togglingDebug, setTogglingDebug] = useState<{
    [key: string]: boolean;
  }>({});
  const [togglingApproval, setTogglingApproval] = useState<{
    [key: string]: boolean;
  }>({});
  const [refreshingFollow, setRefreshingFollow] = useState<{
    [key: string]: boolean;
  }>({});
  const [refreshingAllFollows, setRefreshingAllFollows] = useState(false);
  const [refreshingMod, setRefreshingMod] = useState<{
    [key: string]: boolean;
  }>({});
  const [refreshingAllMods, setRefreshingAllMods] = useState(false);

  useEffect(() => {
    fetchBots();
    // Refresh every 30 seconds
    const interval = setInterval(fetchBots, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBots = async () => {
    try {
      const response = await axios.get("/api/admin/bots/active", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setBots(response.data);
    } catch (error) {
      console.error("Error fetching active bots:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = async (botId: string) => {
    if (reconnecting[botId]) return;

    setReconnecting({ ...reconnecting, [botId]: true });
    try {
      await axios.post(
        `/api/admin/bots/${botId}/reconnect`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      alert("Reconnection initiated successfully");
      // Refresh bot list after reconnection
      setTimeout(fetchBots, 2000);
    } catch (error: any) {
      console.error("Error reconnecting bot:", error);
      alert(error.response?.data?.message || "Failed to reconnect bot");
    } finally {
      setReconnecting({ ...reconnecting, [botId]: false });
    }
  };

  const handleDebugToggle = async (botId: string, currentState: boolean) => {
    if (togglingDebug[botId]) return;

    setTogglingDebug({ ...togglingDebug, [botId]: true });
    try {
      await axios.put(
        `/api/admin/bots/${botId}/debug`,
        { enabled: !currentState },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      // Update local state
      setBots(
        bots.map((bot) =>
          bot.id === botId ? { ...bot, debugEnabled: !currentState } : bot,
        ),
      );
    } catch (error: any) {
      console.error("Error toggling debug mode:", error);
      alert(error.response?.data?.message || "Failed to toggle debug mode");
    } finally {
      setTogglingDebug({ ...togglingDebug, [botId]: false });
    }
  };

  const handleApprovalToggle = async (
    userId: string,
    currentState: boolean,
  ) => {
    if (togglingApproval[userId]) return;

    setTogglingApproval({ ...togglingApproval, [userId]: true });
    try {
      await axios.patch(
        `/api/admin/users/${userId}/approval`,
        { approved: !currentState },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      // Update local state
      setBots(
        bots.map((bot) =>
          bot.userId === userId ? { ...bot, userApproved: !currentState } : bot,
        ),
      );
    } catch (error: any) {
      console.error("Error toggling user approval:", error);
      alert(error.response?.data?.message || "Failed to toggle user approval");
    } finally {
      setTogglingApproval({ ...togglingApproval, [userId]: false });
    }
  };

  const handleRefreshFollowStatus = async (botId: string) => {
    if (refreshingFollow[botId]) return;

    setRefreshingFollow({ ...refreshingFollow, [botId]: true });
    try {
      const response = await axios.post(
        `/api/admin/bots/${botId}/refresh-follow-status`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Update local state with the new follow status
      setBots(
        bots.map((bot) =>
          bot.id === botId
            ? { ...bot, isFollowing: response.data.isFollowing }
            : bot,
        ),
      );

      console.log(response.data.message);
    } catch (error: any) {
      console.error("Error refreshing follow status:", error);
      alert(error.response?.data?.message || "Failed to refresh follow status");
    } finally {
      setRefreshingFollow({ ...refreshingFollow, [botId]: false });
    }
  };

  const handleRefreshAllFollowStatus = async () => {
    if (refreshingAllFollows) return;

    setRefreshingAllFollows(true);
    try {
      await axios.post(
        `/api/admin/bots/refresh-all-follow-status`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Refresh the bot list to get updated statuses
      await fetchBots();
      alert("Follow status refreshed for all bots");
    } catch (error: any) {
      console.error("Error refreshing all follow status:", error);
      alert(error.response?.data?.message || "Failed to refresh follow status");
    } finally {
      setRefreshingAllFollows(false);
    }
  };

  const handleRefreshModStatus = async (botId: string) => {
    if (refreshingMod[botId]) return;

    setRefreshingMod({ ...refreshingMod, [botId]: true });
    try {
      const response = await axios.post(
        `/api/admin/bots/${botId}/refresh-mod-status`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Update local state with the new mod status
      setBots(
        bots.map((bot) =>
          bot.id === botId ? { ...bot, isMod: response.data.isMod } : bot,
        ),
      );

      console.log(response.data.message);
    } catch (error: any) {
      console.error("Error refreshing mod status:", error);
      alert(error.response?.data?.message || "Failed to refresh mod status");
    } finally {
      setRefreshingMod({ ...refreshingMod, [botId]: false });
    }
  };

  const handleRefreshAllModStatus = async () => {
    if (refreshingAllMods) return;

    setRefreshingAllMods(true);
    try {
      await axios.post(
        `/api/admin/bots/refresh-all-mod-status`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Refresh the bot list to get updated statuses
      await fetchBots();
      alert("Mod status refreshed for all bots");
    } catch (error: any) {
      console.error("Error refreshing all mod status:", error);
      alert(error.response?.data?.message || "Failed to refresh mod status");
    } finally {
      setRefreshingAllMods(false);
    }
  };

  const getHealthStatus = (health: BotHealthStatus) => {
    if (!health.ircConnected) {
      return { status: "offline", color: "#ef4444", text: "Offline" };
    }
    if (!health.userTokenValid || !health.eventSubHealthy) {
      return { status: "degraded", color: "#f59e0b", text: "Degraded" };
    }
    if (health.issues.length > 0) {
      return { status: "warning", color: "#fbbf24", text: "Warning" };
    }
    return { status: "healthy", color: "#10b981", text: "Healthy" };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatTimeAgo = (dateString: string | undefined) => {
    if (!dateString) return "Never";

    const now = Date.now();
    const then = new Date(dateString).getTime();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 48) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
        Loading active bots...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
        }}
      >
        <h2 style={{ color: "#e2e8f0", margin: 0 }}>Active Bot Instances</h2>
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <div style={{ color: "#a0aec0", fontSize: "14px" }}>
            Total Active: {bots.length}
          </div>
          <button
            onClick={handleRefreshAllFollowStatus}
            disabled={refreshingAllFollows}
            style={{
              padding: "8px 16px",
              backgroundColor: refreshingAllFollows ? "#718096" : "#9146FF",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: refreshingAllFollows ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}
          >
            {refreshingAllFollows ? "Refreshing..." : "Refresh Follow Status"}
          </button>
          <button
            onClick={handleRefreshAllModStatus}
            disabled={refreshingAllMods}
            style={{
              padding: "8px 16px",
              backgroundColor: refreshingAllMods ? "#718096" : "#9146FF",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: refreshingAllMods ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}
          >
            {refreshingAllMods ? "Refreshing..." : "Refresh Mod Status"}
          </button>
          <button
            onClick={fetchBots}
            style={{
              padding: "8px 16px",
              backgroundColor: "#4a5568",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Refresh List
          </button>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#2d3748",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #4a5568",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#1a202c" }}>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Channel
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Following
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Mod
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Bot Name
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Status
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Last Activity
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Health Issues
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Last Updated
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Actions
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Approved
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#cbd5e0",
                  fontWeight: "600",
                  borderBottom: "1px solid #4a5568",
                }}
              >
                Debug
              </th>
            </tr>
          </thead>
          <tbody>
            {bots.map((bot) => {
              const healthStatus = getHealthStatus(bot.health);
              return (
                <tr
                  key={bot.id}
                  style={{
                    borderBottom: "1px solid #4a5568",
                    backgroundColor: "transparent",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#374151")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <td style={{ padding: "12px" }}>
                    <div>
                      <span style={{ color: "#e2e8f0" }}>{bot.username}</span>
                      <br />
                      <span style={{ color: "#718096", fontSize: "13px" }}>
                        {bot.channel.startsWith("#")
                          ? bot.channel
                          : `#${bot.channel}`}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={() => handleRefreshFollowStatus(bot.id)}
                        disabled={refreshingFollow[bot.id]}
                        style={{
                          background: "none",
                          border: "1px solid",
                          borderColor: bot.isFollowing ? "#10b981" : "#f59e0b",
                          color: bot.isFollowing ? "#10b981" : "#f59e0b",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          cursor: refreshingFollow[bot.id]
                            ? "not-allowed"
                            : "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                        title="Click to refresh follow status"
                      >
                        {refreshingFollow[bot.id]
                          ? "..."
                          : bot.isFollowing
                            ? "Yes"
                            : "No"}
                      </button>
                      {!bot.isFollowing && (
                        <a
                          href={`https://twitch.tv/${bot.channel.replace("#", "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            background: "#10b981",
                            border: "none",
                            color: "white",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                            textDecoration: "none",
                            display: "inline-block",
                          }}
                          title="Open Twitch channel to manually follow (API no longer supports auto-follow)"
                        >
                          Follow{" "}
                          <Icon
                            name="arrow-up-right"
                            size={14}
                            style={{ verticalAlign: "-2px", marginLeft: "4px" }}
                          />
                        </a>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <button
                      onClick={() => handleRefreshModStatus(bot.id)}
                      disabled={refreshingMod[bot.id]}
                      style={{
                        background: "none",
                        border: "1px solid",
                        borderColor: bot.isMod ? "#10b981" : "#718096",
                        color: bot.isMod ? "#10b981" : "#718096",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        cursor: refreshingMod[bot.id]
                          ? "not-allowed"
                          : "pointer",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                      title="Click to refresh mod status"
                    >
                      {refreshingMod[bot.id] ? "..." : bot.isMod ? "Yes" : "No"}
                    </button>
                  </td>
                  <td style={{ padding: "12px", color: "#e2e8f0" }}>
                    {bot.botUsername}
                  </td>
                  <td style={{ padding: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: healthStatus.color,
                        }}
                      />
                      <span style={{ color: healthStatus.color }}>
                        {healthStatus.text}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "13px",
                    }}
                  >
                    {formatTimeAgo(bot.lastActivity)}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "13px",
                    }}
                  >
                    {bot.health.issues.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {bot.health.issues.map((issue, idx) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    ) : (
                      <span style={{ color: "#4ade80" }}>No issues</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      color: "#a0aec0",
                      fontSize: "13px",
                    }}
                  >
                    {bot.health.lastPing
                      ? formatDate(bot.health.lastPing)
                      : "Never"}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <button
                      onClick={() => handleReconnect(bot.id)}
                      disabled={reconnecting[bot.id]}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: reconnecting[bot.id]
                          ? "#6B7280"
                          : "#9146FF",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: reconnecting[bot.id]
                          ? "not-allowed"
                          : "pointer",
                        fontSize: "12px",
                        opacity: reconnecting[bot.id] ? 0.6 : 1,
                      }}
                    >
                      {reconnecting[bot.id]
                        ? "Reconnecting..."
                        : "Reconnect Bot"}
                    </button>
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "44px",
                          height: "24px",
                          cursor: togglingApproval[bot.userId]
                            ? "not-allowed"
                            : "pointer",
                          opacity: togglingApproval[bot.userId] ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={bot.userApproved !== false}
                          onChange={() =>
                            handleApprovalToggle(
                              bot.userId,
                              bot.userApproved !== false,
                            )
                          }
                          disabled={togglingApproval[bot.userId]}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor:
                              bot.userApproved !== false
                                ? "#48bb78"
                                : "#718096",
                            borderRadius: "24px",
                            transition: "0.4s",
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: bot.userApproved !== false ? "22px" : "2px",
                              bottom: "2px",
                              width: "20px",
                              height: "20px",
                              backgroundColor: "white",
                              borderRadius: "50%",
                              transition: "0.4s",
                            }}
                          />
                        </span>
                      </label>
                    </div>
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "44px",
                          height: "24px",
                          cursor: togglingDebug[bot.id]
                            ? "not-allowed"
                            : "pointer",
                          opacity: togglingDebug[bot.id] ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={bot.debugEnabled || false}
                          onChange={() =>
                            handleDebugToggle(bot.id, bot.debugEnabled || false)
                          }
                          disabled={togglingDebug[bot.id]}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: bot.debugEnabled
                              ? "#9146FF"
                              : "#4a5568",
                            borderRadius: "24px",
                            transition: "0.4s",
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: bot.debugEnabled ? "22px" : "2px",
                              bottom: "2px",
                              width: "20px",
                              height: "20px",
                              backgroundColor: "white",
                              borderRadius: "50%",
                              transition: "0.4s",
                            }}
                          />
                        </span>
                      </label>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {bots.length === 0 && (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#a0aec0",
            }}
          >
            No active bot instances found
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: "20px",
          padding: "15px",
          backgroundColor: "#1a202c",
          borderRadius: "8px",
          border: "1px solid #4a5568",
          fontSize: "13px",
          color: "#cbd5e0",
        }}
      >
        <h4 style={{ color: "#9146FF", marginTop: 0, marginBottom: "10px" }}>
          Status Legend
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "10px",
          }}
        >
          <div>
            <span style={{ color: "#10b981" }}>● Healthy:</span> Bot is fully
            operational
          </div>
          <div>
            <span style={{ color: "#f59e0b" }}>● Degraded:</span> Bot connected
            but missing user auth
          </div>
          <div>
            <span style={{ color: "#fbbf24" }}>● Warning:</span> Bot has minor
            issues
          </div>
          <div>
            <span style={{ color: "#ef4444" }}>● Offline:</span> Bot is not
            connected
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <span style={{ color: "#9146FF" }}>● Debug Enabled:</span> Log
            context window for all bot interactions
          </div>
        </div>
        <p style={{ marginTop: "15px", marginBottom: 0, color: "#a0aec0" }}>
          <strong>Note:</strong> &quot;Reconnect Bot&quot; will refresh user
          tokens and re-establish EventSub subscriptions. This requires valid
          user refresh tokens and bot credentials.
        </p>
      </div>
    </div>
  );
};

export default BotList;
